import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiCall from "./../../API/API";
import Popup from "reactjs-popup";
import { HiDownload } from "react-icons/hi";
import { useDropzone } from "react-dropzone";
import imageCompression from "browser-image-compression";
import { MdArrowBack } from "react-icons/md";
import { FaExpandAlt } from "react-icons/fa";
import { MdOutlineRotate90DegreesCcw } from "react-icons/md";
import "./KycControls.css";

const DocumentImageCell = ({ src, label, onImageClick }) => {
  if (!src) return null;

  return (
    <>
      <img
        src={src}
        alt={label}
        style={{
          maxWidth: "100px",
          maxHeight: "100px",
          cursor: "pointer",
        }}
        onClick={() => onImageClick(src)}
      />
      <Popup trigger={<button className="block"><FaExpandAlt /></button>} modal closeOnDocumentClick>
        {(close) => (
          <div className="popup-content">
            <button className="close-btn" onClick={close}>
              &times;
            </button>
            <img src={src} alt={label} className="popImg" />
            <button
              className="download-btn"
              onClick={() => {
                const link = document.createElement("a");
                link.href = src;
                link.download = `${label}.jpg`;
                link.click();
              }}
            >
              <HiDownload />
            </button>
          </div>
        )}
      </Popup>
    </>
  );
};

const AadharKycControls = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { username, currentPage, userList = [] } = location.state || {};
  const [currentUser] = useState(
    username || (userList.length ? userList[0] : "")
  );
  const [aadharNumber, setAadharNumber] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [aadharFrontFiles, setAadharFrontFiles] = useState([]);
  const [aadharBackFiles, setAadharBackFiles] = useState([]);
  const [userSignFiles, setUserSignFiles] = useState([]);
  const [previews, setPreviews] = useState({
    aadharFront: [],
    aadharBack: [],
    userSign: [],
  });
  const [kyc, setKyc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState({ aadhar: "" });
  const [isAadharEdit, setIsAadharEdit] = useState(false);
  const [showAadharRejectBox, setShowAadharRejectBox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [zoomInput, setZoomInput] = useState(100);

  useEffect(() => {
    if (currentUser) {
      fetchKYC(currentUser);
    }
  }, [currentUser]);

  const fetchKYC = async (searchUsername) => {
    const queryString = new URLSearchParams({ search: searchUsername }).toString();
    const { data, error } = await apiCall(
      "GET",
      `/all-kyc-request?${queryString}`,
      null,
      false
    );
    if (error) {
      toast.error(error.message);
    } else {
      setKyc(data?.data[0]);
    }
  };

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1, 3));
    setZoomInput((prev) => Math.min(prev + 10, 300));
  };

  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0));
    setZoomInput((prev) => Math.max(prev - 10, 0));
  };

  const handleZoomInputChange = (e) => {
    const value = Math.min(Math.max(Number(e.target.value), 0), 300);
    setZoomInput(value);
    setScale(value / 100);
  };

  const handleRotate = () => {
    setRotation((prevRotation) => prevRotation + 90);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleCloseViewer = () => {
    setSelectedImage(null);
  };

  const handleAadhar = async () => {
    try {
      const formData = new FormData();
      formData.append(
        "aadharNumber",
        aadharNumber || kyc?.aadharDetails?.aadharNumber
      );
      formData.append(
        "fatherName",
        fatherName || kyc?.aadharDetails?.fatherName
      );
      formData.append("address", address || kyc?.aadharDetails?.address);
      formData.append("pincode", pincode || kyc?.aadharDetails?.pincode);
      formData.append("uid", kyc?.uid);

      if (aadharFrontFiles.length > 0) {
        aadharFrontFiles.forEach((file) => {
          formData.append("documentFront", file);
        });
      } else if (kyc?.aadharDetails?.documentFront) {
        formData.append("documentFront", kyc.aadharDetails.documentFront);
      }

      if (aadharBackFiles.length > 0) {
        aadharBackFiles.forEach((file) => {
          formData.append("documentBack", file);
        });
      } else if (kyc?.aadharDetails?.documentBack) {
        formData.append("documentBack", kyc.aadharDetails.documentBack);
      }

      if (userSignFiles.length > 0) {
        userSignFiles.forEach((file) => {
          formData.append("userSign", file);
        });
      } else if (kyc?.aadharDetails?.userSign) {
        formData.append("userSign", kyc.aadharDetails.userSign);
      }

      const { data, error } = await apiCall(
        "POST",
        "/update-aadhar-kyc",
        formData
      );
      if (error) {
        toast.error(error.message || "An error occurred");
      } else {
        toast.success(data.message || "Aadhar details updated successfully!");
        setIsAadharEdit(false);
        fetchKYC(currentUser);
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const onDrop = async (acceptedFiles, type) => {
    const compressedFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };
        try {
          return await imageCompression(file, options);
        } catch (err) {
          console.error("Error compressing image:", err);
          return file;
        }
      })
    );

    if (type === "aadharFront") {
      setAadharFrontFiles(compressedFiles);
      setPreviews((prev) => ({
        ...prev,
        aadharFront: compressedFiles.map((file) => URL.createObjectURL(file)),
      }));
    } else if (type === "aadharBack") {
      setAadharBackFiles(compressedFiles);
      setPreviews((prev) => ({
        ...prev,
        aadharBack: compressedFiles.map((file) => URL.createObjectURL(file)),
      }));
    } else if (type === "userSign") {
      setUserSignFiles(compressedFiles);
      setPreviews((prev) => ({
        ...prev,
        userSign: compressedFiles.map((file) => URL.createObjectURL(file)),
      }));
    }
  };

  const { getRootProps: getAadharFrontRootProps, getInputProps: getAadharFrontInputProps } =
    useDropzone({
      onDrop: (acceptedFiles) => onDrop(acceptedFiles, "aadharFront"),
    });

  const { getRootProps: getAadharBackRootProps, getInputProps: getAadharBackInputProps } =
    useDropzone({
      onDrop: (acceptedFiles) => onDrop(acceptedFiles, "aadharBack"),
    });

  const { getRootProps: getUserSignRootProps, getInputProps: getUserSignInputProps } =
    useDropzone({
      onDrop: (acceptedFiles) => onDrop(acceptedFiles, "userSign"),
    });

  const handleApprove = async (uid, kycType) => {
    try {
      const { data, error } = await apiCall("POST", "/approve-kyc-request", {
        uid,
        kycType,
      });
      if (error) {
        toast.error(error.message || "An error occurred");
      } else {
        toast.success(data.message || "Request Approved");
        if (kycType === "aadhar") {
          setShowAadharRejectBox(false);
          setRejectionReason((prev) => ({ ...prev, aadhar: "" }));
        }
        fetchKYC(currentUser);
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleReject = async (uid, kycType) => {
    try {
      const { data, error } = await apiCall("POST", "/reject-kyc-request", {
        uid,
        kycType,
        reason: rejectionReason[kycType],
      });
      if (error) {
        toast.error(error.message || "An error occurred");
      } else {
        toast.success(data.message || "Request Rejected");
        if (kycType === "aadhar") {
          setShowAadharRejectBox(false);
          setRejectionReason((prev) => ({ ...prev, aadhar: "" }));
        }
        fetchKYC(currentUser);
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleGoBack = () => {
    navigate("/dashboard/aadhar_kyc_detail", {
      state: { currentPage },
    });
  };

  const getAadharStatusBadgeStyle = (statusType) => {
    const baseStyle = {
      display: "inline-block",
      padding: "6px 12px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
    };

    const statusStyles = {
      pending: {
        color: "#856404",
        background: "#fff3cd",
        border: "1px solid #ffeeba",
      },
      approved: {
        color: "#155724",
        background: "#d4edda",
        border: "1px solid #c3e6cb",
      },
      rejected: {
        color: "#721c24",
        background: "#f8d7da",
        border: "1px solid #f5c6cb",
      },
      approvalPending: {
        color: "#0c5460",
        background: "#d1ecf1",
        border: "1px solid #bee5eb",
      },
    };

    return { ...baseStyle, ...statusStyles[statusType] };
  };

  const renderAadharApprovalActions = () => (
    <form>
      <span style={getAadharStatusBadgeStyle("approvalPending")}>
        Approval Pending
      </span>
      <div
        className="form-group"
        style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}
      >
        <button
          className="action-btn"
          onClick={(e) => {
            e.preventDefault();
            handleApprove(kyc?.uid, "aadhar");
          }}
        >
          Approve Aadhar
        </button>
        <button
          className="action-btn"
          onClick={(e) => {
            e.preventDefault();
            setShowAadharRejectBox(true);
          }}
        >
          Reject Aadhar
        </button>
      </div>
      {!showAadharRejectBox ? null : (
        <>
          <div className="form-group">
            <label>Rejection Reason (only if rejecting)</label>
            <textarea
              className="kyc-input"
              placeholder="Enter reason for rejection"
              value={rejectionReason.aadhar}
              onChange={(e) =>
                setRejectionReason({
                  ...rejectionReason,
                  aadhar: e.target.value,
                })
              }
            ></textarea>
          </div>
          <div className="form-group">
            <button
              className="action-btn"
              disabled={!rejectionReason.aadhar?.trim()}
              style={{
                opacity: rejectionReason.aadhar?.trim() ? 1 : 0.6,
                cursor: rejectionReason.aadhar?.trim() ? "pointer" : "not-allowed",
              }}
              onClick={(e) => {
                e.preventDefault();
                if (!rejectionReason.aadhar?.trim()) return;
                handleReject(kyc?.uid, "aadhar");
              }}
            >
              Confirm Reject
            </button>
            <button
              className="action-btn"
              onClick={(e) => {
                e.preventDefault();
                setShowAadharRejectBox(false);
                setRejectionReason((prev) => ({ ...prev, aadhar: "" }));
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </form>
  );

  const renderAadharStatusSection = () => {
    const aadharStatus = Number(kyc?.isApproved?.aadhar);

    if (aadharStatus === 3) return renderAadharApprovalActions();

    if (aadharStatus === 1) {
      return (
        <p id="KycDetail" style={getAadharStatusBadgeStyle("approved")}>
          Approved
        </p>
      );
    }

    if (aadharStatus === 2) {
      return (
        <>
          <p id="KycDetail" style={getAadharStatusBadgeStyle("rejected")}>
            Rejected
          </p>
          {kyc?.aadharDetails?.reason ? (
            <p style={{ marginTop: "8px" }}>Reason: {kyc?.aadharDetails?.reason}</p>
          ) : null}
        </>
      );
    }

    return (
      <p id="KycDetail" style={getAadharStatusBadgeStyle("pending")}>
        Pending From User
      </p>
    );
  };

  const hasAadharDetails =
    kyc?.aadharDetails?.aadharNumber ||
    kyc?.aadharDetails?.fatherName ||
    kyc?.aadharDetails?.address ||
    kyc?.aadharDetails?.pincode ||
    kyc?.aadharDetails?.documentFront ||
    kyc?.aadharDetails?.documentBack ||
    kyc?.aadharDetails?.userSign;

  return (
    <React.Fragment>
      <Container>
        <div className={`details-table ${selectedImage ? "shifted" : ""}`}>
          <div onClick={handleGoBack} className="backButton">
            <MdArrowBack size={22} style={{ marginRight: "3px" }} />
            <span>Back</span>
          </div>
          <div className="content">
            <h3>Aadhar KYC Details</h3>
            <hr />
            <Row id="directTabRow">
              <Col xs="12" sm="12" md="12" lg="12">
                <div className="table-responsive">
                  <h4>User Details</h4>
                  <table className="detailTable">
                    <tbody>
                      <tr>
                        <th>Name</th>
                        <td>:</td>
                        <td>{kyc?.name}</td>
                      </tr>
                      <tr>
                        <th>Username</th>
                        <td>:</td>
                        <td>{kyc?.username}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>

            <Row id="directTabRow">
              <Col xs="12" sm="12" md="8" lg="8">
                <div className="table-responsive">
                  <h4>Aadhar Details</h4>
                  {/* {isAadharEdit ? (
                    <table className="detailTable">
                      <tbody>
                        <tr>
                          <th>Aadhar No</th>
                          <td>:</td>
                          <td>
                            <input
                              type="text"
                              className="kyc-input"
                              value={aadharNumber || kyc?.aadharDetails?.aadharNumber}
                              onChange={(e) => setAadharNumber(e.target.value)}
                              required
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>{kyc?.aadharDetails?.relation === "husband" ? "Husband Name" : "Father Name"}</th>
                          <td>:</td>
                          <td>
                            <input
                              type="text"
                              className="kyc-input"
                              value={fatherName || kyc?.aadharDetails?.fatherName}
                              onChange={(e) => setFatherName(e.target.value)}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Address</th>
                          <td>:</td>
                          <td>
                            <textarea
                              className="kyc-input"
                              rows={3}
                              value={address || kyc?.aadharDetails?.address}
                              onChange={(e) => setAddress(e.target.value)}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Pin Code</th>
                          <td>:</td>
                          <td>
                            <input
                              type="text"
                              className="kyc-input"
                              value={pincode || kyc?.aadharDetails?.pincode}
                              onChange={(e) => setPincode(e.target.value)}
                              maxLength={6}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Document (Front)</th>
                          <td>:</td>
                          <td>
                            <div className="file-upload-container">
                              <div {...getAadharFrontRootProps({ className: "dropzone" })}>
                                <input {...getAadharFrontInputProps()} />
                                <p>
                                  Drag &apos;n&apos; drop Aadhar front here, or click to select
                                </p>
                              </div>
                              {previews.aadharFront.length > 0
                                ? previews.aadharFront.map((url, index) => (
                                    <img
                                      key={index}
                                      src={url}
                                      alt={`preview-front-${index}`}
                                      style={{ width: 150, height: 100, margin: 10 }}
                                    />
                                  ))
                                : kyc?.aadharDetails?.documentFront ? (
                                    <img
                                      src={kyc.aadharDetails.documentFront}
                                      alt="Aadhar Front"
                                      style={{ width: 150, height: 100, margin: 10 }}
                                    />
                                  ) : null}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th>Document (Back)</th>
                          <td>:</td>
                          <td>
                            <div className="file-upload-container">
                              <div {...getAadharBackRootProps({ className: "dropzone" })}>
                                <input {...getAadharBackInputProps()} />
                                <p>
                                  Drag &apos;n&apos; drop Aadhar back here, or click to select
                                </p>
                              </div>
                              {previews.aadharBack.length > 0
                                ? previews.aadharBack.map((url, index) => (
                                    <img
                                      key={index}
                                      src={url}
                                      alt={`preview-back-${index}`}
                                      style={{ width: 150, height: 100, margin: 10 }}
                                    />
                                  ))
                                : kyc?.aadharDetails?.documentBack ? (
                                    <img
                                      src={kyc.aadharDetails.documentBack}
                                      alt="Aadhar Back"
                                      style={{ width: 150, height: 100, margin: 10 }}
                                    />
                                  ) : null}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th>User Sign Image</th>
                          <td>:</td>
                          <td>
                            <div className="file-upload-container">
                              <div {...getUserSignRootProps({ className: "dropzone" })}>
                                <input {...getUserSignInputProps()} />
                                <p>
                                  Drag &apos;n&apos; drop user sign image here, or click to select
                                </p>
                              </div>
                              {previews.userSign.length > 0
                                ? previews.userSign.map((url, index) => (
                                    <img
                                      key={index}
                                      src={url}
                                      alt={`preview-sign-${index}`}
                                      style={{ width: 150, height: 100, margin: 10 }}
                                    />
                                  ))
                                : kyc?.aadharDetails?.userSign ? (
                                    <img
                                      src={kyc.aadharDetails.userSign}
                                      alt="User Sign"
                                      style={{ width: 150, height: 100, margin: 10 }}
                                    />
                                  ) : null}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  ) : */ hasAadharDetails ? (
                    <table className="detailTable">
                      <tbody>
                        <tr>
                          <th>Aadhar No</th>
                          <td>:</td>
                          <td>{kyc?.aadharDetails?.aadharNumber}</td>
                        </tr>
                        <tr>
                          <th>{kyc?.aadharDetails?.relation === "husband" ? "Husband Name" : "Father Name"}</th>
                          <td>:</td>
                          <td>{kyc?.aadharDetails?.fatherName}</td>
                        </tr>
                        <tr>
                          <th>Address</th>
                          <td>:</td>
                          <td>{kyc?.aadharDetails?.address}</td>
                        </tr>
                        <tr>
                          <th>Pin Code</th>
                          <td>:</td>
                          <td>{kyc?.aadharDetails?.pincode}</td>
                        </tr>
                        <tr>
                          <th>Document (Front)</th>
                          <td>:</td>
                          <td>
                            <DocumentImageCell
                              src={kyc?.aadharDetails?.documentFront}
                              label="Aadhar Front"
                              onImageClick={handleImageClick}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>Document (Back)</th>
                          <td>:</td>
                          <td>
                            <DocumentImageCell
                              src={kyc?.aadharDetails?.documentBack}
                              label="Aadhar Back"
                              onImageClick={handleImageClick}
                            />
                          </td>
                        </tr>
                        {/* Signature is no longer collected; shown only for older submissions */}
                        {kyc?.aadharDetails?.userSign && (
                        <tr>
                          <th>User Sign Image</th>
                          <td>:</td>
                          <td>
                            <DocumentImageCell
                              src={kyc?.aadharDetails?.userSign}
                              label="User Sign"
                              onImageClick={handleImageClick}
                            />
                          </td>
                        </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <p>No details found</p>
                  )}
                </div>
              </Col>
              <Col xs="12" sm="12" md="4" lg="4">
                {/* {!isAadharEdit ? (
                  <Button
                    variant="primary"
                    onClick={() => setIsAadharEdit(true)}
                    className="editBtn"
                    style={{ marginBottom: "10px" }}
                  >
                    Edit Aadhar
                  </Button>
                ) : null}
                {isAadharEdit ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAadhar();
                      }}
                      className="editBtn"
                    >
                      Save
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setIsAadharEdit(false)}
                      className="editBtn"
                    >
                      Cancel
                    </Button>
                  </>
                ) : null} */}

                {renderAadharStatusSection()}
              </Col>
            </Row>
          </div>
        </div>
        {selectedImage && (
          <div className="side-image-viewer">
            <button className="close-btn2" onClick={handleCloseViewer}>
              &times;
            </button>

            <div className="zoom-rotate-controls">
              <button className="zoom-button" onClick={handleZoomOut}>
                -
              </button>
              <input
                type="number"
                value={zoomInput}
                onChange={handleZoomInputChange}
                className="zoom-percentage-input"
                min="0"
                max="300"
              />
              <button className="zoom-button" onClick={handleZoomIn}>
                +
              </button>
              <button className="rotate-button" onClick={handleRotate}>
                <MdOutlineRotate90DegreesCcw />
              </button>
            </div>

            <div className="image-container">
              <img
                src={selectedImage}
                alt="Selected"
                className="full-image"
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  transition: "transform 0.3s ease",
                }}
              />
            </div>
          </div>
        )}
      </Container>
    </React.Fragment>
  );
};

export default AadharKycControls;
